import useWindowSize from '@rehooks/window-size';
import cx from 'classnames';
import useToggleAndOutClick from 'hooks/useToggleAndOutClick';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { createInvoice } from 'modules/profileLegasy/actions';
import BillCreationCard from 'modules/profileLegasy/components/BillCreationCard/BillCreationCard';
import HintCommissionAndLimits from 'modules/profileLegasy/components/Hint/HintCommissionAndLimits';
import HintHowDoesItWork from 'modules/profileLegasy/components/Hint/HintHowDoesItWork';
import HintHowToKnowIfCheckPayed from 'modules/profileLegasy/components/Hint/HintHowToKnowIfCheckPayed';
import HintWhereAreMyRequests from 'modules/profileLegasy/components/Hint/HintWhereAreMyRequests';
import { getUserLimits } from 'modules/profileLegasy/selectors';
import HintsList from 'modules/shared/components/HintsList/HintsList';

import { CONSISTENT_ANIMATIONS_DELAY, PARTLY_YES, YES } from 'constants/index';

import { ReactComponent as IconBag } from 'static/assets/icon-bag.svg';
import profileLegasyBg from 'static/assets/profile-bg.png';
import { formatDateRFC3339WithCurrentTime } from '../../../../helpers/formatDateRFC3339';

// Styles
import './BillCreation.scss';
import { checkPaymentEnabled, PaymentDisabledPopup } from '../../../../tools/utils/payments';
import { OnlyAuthorizedIdentifyPopup } from '../../../../tools/utils/only-authorized';

const LINK_TAB = 'LINK_TAB';
const EMAIL_TAB = 'EMAIL_TAB';

const BillCreation = ({ dispatch, userTaxStatus, szStatusFromDb, userLimits, user }) => {
  const windowSize = useWindowSize();
  const [redirectOnLk, setRedirectOnLk] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isDropOpen, dropEl, toggleDrop, closeExplicitly] = useToggleAndOutClick();
  const [currentOpenPopupId, setCurrentOpenPopupId] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isBillCreationSuccess, setIsBillCreationSuccess] = useState(false);
  const [isBillCreationFail, setIsBillCreationFail] = useState(false);
  const [isFailedOverLimit, setIsFailedOverLimit] = useState(false);

  const [currentTabId, setCurrentTabId] = useState(LINK_TAB);
  const [link, setLink] = useState('');

  const handleClickHowDoesItWorkButton = () => {
    toggleDrop();
    setCurrentOpenPopupId(1);
  };

  const handleClickWhereAreMyRequestsButton = () => {
    toggleDrop();
    setCurrentOpenPopupId(2);
  };

  const handleClickHowToKnowIfRequestPayedButton = () => {
    toggleDrop();
    setCurrentOpenPopupId(3);
  };

  const handleClickCommissionAndLimitsButton = () => {
    toggleDrop();
    setCurrentOpenPopupId(4);
  };

  const closeHintHandler = () => {
    closeExplicitly();
    setCurrentOpenPopupId(null);
  };

  /** Выставляем счёт */
  const handleSendRequestClick = (commentary, date, email, sum) => {
    setIsLoading(true);

    dispatch(
      createInvoice(
        commentary,
        formatDateRFC3339WithCurrentTime(date),
        currentTabId === EMAIL_TAB ? email : '',
        +sum * 100,
      ),
    )
      .then((res) => {
        setIsBillCreationSuccess(true);
        if (res && res.result && res.result.message) {
          setLink(`${window.location.origin}/invoice/${res.result.message}`);
        }
      })
      .catch((err) => {
        if (err && err.data === 'pay-it.over-daily-limit') {
          setIsFailedOverLimit(true);
        }
        setIsBillCreationFail(true);
      })
      .then(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsRendered(true);
  }, []);

  /**
   * Проверяекм, сз ли юзер:
   * если у него статус не YES и при этом нет
   * дополнительного статуса szStatusFromDb = PARTLY_YES,
   * то редиректим на личный кабинет
   * */
  useEffect(() => {
    if (
      !userTaxStatus ||
      (userTaxStatus !== YES && (!szStatusFromDb || szStatusFromDb !== PARTLY_YES))
    ) {
      setRedirectOnLk(true);
    }
  }, [userTaxStatus, szStatusFromDb]);

  const hints = [
    {
      id: 1,
      title: 'Как это работает?',
      titleForPopup: 'Как это работает?',
      action: handleClickHowDoesItWorkButton,
      openCondition: currentOpenPopupId === 1 && isDropOpen,
      popup: <HintHowDoesItWork />,
      dropEl,
    },
    {
      id: 2,
      title: 'Где хранятся мои запросы?',
      titleForPopup: 'Где хранятся мои запросы?',
      action: handleClickWhereAreMyRequestsButton,
      openCondition: currentOpenPopupId === 2 && isDropOpen,
      popup: <HintWhereAreMyRequests />,
      dropEl,
    },
    {
      id: 3,
      title: 'Как узнать, что мой счет оплачен?',
      titleForPopup: 'Как узнать, что мой счет оплачен?',
      action: handleClickHowToKnowIfRequestPayedButton,
      openCondition: currentOpenPopupId === 3 && isDropOpen,
      popup: <HintHowToKnowIfCheckPayed />,
      dropEl,
    },
    {
      id: 4,
      title: 'Комиссия и лимиты',
      titleForPopup: 'Комиссия и лимиты',
      action: handleClickCommissionAndLimitsButton,
      openCondition: currentOpenPopupId === 4 && isDropOpen,
      popup: (
        <HintCommissionAndLimits
          additionalText={'Также действует лимит на создание счетов на оплату — 40 раз в сутки.'}
        />
      ),
      dropEl,
    },
  ];

  /** Редирект на ЛК */
  if (redirectOnLk) {
    return <Redirect to="/lk" />;
  }

  return (
    <>
      <PaymentDisabledPopup redirect={'/lk'} />
      {checkPaymentEnabled() && <OnlyAuthorizedIdentifyPopup user={user} redirect={'/lk'} />}
      <div className="bill-creation">
        {/* ЗАГОЛОВОК ПРИ ШИРИНЕ ЭКРАНА > 980 */}
        <CSSTransition
          classNames="animation-from-bottom-to-top"
          in={isRendered && windowSize.innerWidth > 980}
          timeout={CONSISTENT_ANIMATIONS_DELAY[0]}
          unmountOnExit
        >
          <div className="bill-creation__top">
            {!isBillCreationSuccess && !isBillCreationFail && (
              <h1 className="bill-creation__title">Выставить счет</h1>
            )}

            {isBillCreationSuccess && <h1 className="bill-creation__title">Выставлен счет</h1>}

            {isBillCreationFail && <h1 className="bill-creation__title">Счет не выставлен</h1>}
          </div>
        </CSSTransition>
        {/* /ЗАГОЛОВОК ПРИ ШИРИНЕ ЭКРАНА > 980 */}

        {/* ХЭДЕР С БЭКГРАУНДОМ ПРИ ШИРИНЕ ЭКРАНА < 980 */}
        {windowSize.innerWidth <= 980 && (
          <div
            className={cx('bill-creation__header', {
              'bill-creation__header_fail': isBillCreationFail,
            })}
          >
            {!isBillCreationFail && (
              <div className="bill-creation__header-icon-wrap">
                <IconBag className="bill-creation__header-icon" />
              </div>
            )}

            {!isBillCreationSuccess && !isBillCreationFail && (
              <h1 className="bill-creation__header-title">Выставить счет</h1>
            )}

            {isBillCreationSuccess && (
              <h1 className="bill-creation__header-title">Выставлен счет</h1>
            )}

            {isBillCreationFail && (
              <h1 className="bill-creation__header-title">Счет не выставлен</h1>
            )}

            <img
              src={profileLegasyBg}
              alt="Фоновая картинка"
              className="bill-creation__header-bg"
            />
          </div>
        )}
        {/* /ХЭДЕР С БЭКГРАУНДОМ ПРИ ШИРИНЕ ЭКРАНА < 980 */}

        <div className="bill-creation__content">
          {/* КАРТОЧКА СОЗДАНИЯ СЧЕТА */}
          <CSSTransition
            classNames="animation-from-bottom-to-top"
            in={isRendered}
            timeout={CONSISTENT_ANIMATIONS_DELAY[1]}
            unmountOnExit
          >
            <div className="bill-creation__card-wrap animation-from-bottom-to-top">
              <BillCreationCard
                className="bill-creation__card"
                handleSubmitClick={handleSendRequestClick}
                isSucceed={isBillCreationSuccess}
                isFail={isBillCreationFail}
                isLoading={isLoading}
                link={link}
                isFailedOverLimit={isFailedOverLimit}
                linkTabCLick={() => setCurrentTabId(LINK_TAB)}
                emailTabCLick={() => setCurrentTabId(EMAIL_TAB)}
                currentTabId={currentTabId}
                userLimits={userLimits}
              />
              {isBillCreationSuccess && (
                <div className="bill-creation__card-link-wrap">
                  <Link to="/lk" className="bill-creation__card-link">
                    Перейти в кошелек
                  </Link>
                </div>
              )}
            </div>
          </CSSTransition>
          {/* /КАРТОЧКА СОЗДАНИЯ СЧЕТА */}

          {/* ПОЛЕЗНЫЕ ССЫЛКИ */}
          <CSSTransition
            classNames="animation-from-bottom-to-top"
            in={isRendered}
            timeout={CONSISTENT_ANIMATIONS_DELAY[2]}
            unmountOnExit
          >
            <div className="bill-creation__useful-links">
              <HintsList hints={hints} closeHintHandler={closeHintHandler} />
            </div>
          </CSSTransition>
          {/* /ПОЛЕЗНЫЕ ССЫЛКИ */}
        </div>
      </div>
    </>
  );
};

BillCreation.propTypes = {
  dispatch: PropTypes.func.isRequired,
  userTaxStatus: PropTypes.string,
  szStatusFromDb: PropTypes.string,
  userLimits: PropTypes.shape({
    minSum: PropTypes.number.isRequired,
    balanceLimit: PropTypes.number.isRequired,
  }).isRequired,
  user: PropTypes.object.isRequired,
};

BillCreation.defaultProps = {
  userTaxStatus: null,
  szStatusFromDb: '',
};

const mapStateToProps = (state) => ({
  userTaxStatus:
    state.auth.user && state.auth.user.taxStatus && state.auth.user.taxStatus.status
      ? state.auth.user.taxStatus.status
      : null,
  szStatusFromDb: state.auth.szStatusFromDb,
  userLimits: getUserLimits()(state),
  user: state.auth.user,
});

export default connect(mapStateToProps)(BillCreation);
